import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Building2, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export function WorkspaceSelector() {
  const { currentWorkspace, workspaces, switchWorkspace } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedId(null); // Reset selection when closing
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (currentWorkspace) {
      setSelectedId(currentWorkspace.id);
    }
  }, [currentWorkspace]);

  const handleWorkspaceClick = (workspaceId: string) => {
    setSelectedId(workspaceId);
  };

  const handleSwitchConfirm = () => {
    if (selectedId && selectedId !== currentWorkspace?.id) {
      switchWorkspace(selectedId);
    }
    setIsOpen(false);
  };

  if (!currentWorkspace) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-50 border border-gray-200 min-w-[200px]"
      >
        <div className="flex-shrink-0">
          {currentWorkspace.logo ? (
            <img
              src={currentWorkspace.logo}
              alt={currentWorkspace.name}
              className="h-6 w-6 rounded object-cover"
            />
          ) : (
            <Building2 className="h-5 w-5 text-gray-400" />
          )}
        </div>
        <span className="text-sm font-medium text-gray-700 flex-1 text-left truncate">
          {currentWorkspace.name}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-72 bg-white rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
          <div className="p-2">
            <div className="space-y-1">
              {workspaces.map((workspace) => (
                <button
                  key={workspace.id}
                  onClick={() => handleWorkspaceClick(workspace.id)}
                  className={`w-full text-left px-3 py-2 text-sm rounded-lg flex items-center space-x-3
                    ${workspace.id === selectedId
                      ? 'bg-green-50 text-green-700'
                      : 'text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <div className="flex-shrink-0">
                    {workspace.logo ? (
                      <img
                        src={workspace.logo}
                        alt={workspace.name}
                        className="h-6 w-6 rounded object-cover"
                      />
                    ) : (
                      <Building2 className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                  <span className="flex-1 truncate">{workspace.name}</span>
                  {workspace.id === selectedId && (
                    <Check className="h-4 w-4 text-green-600" />
                  )}
                </button>
              ))}
            </div>
            
            <div className="border-t border-gray-100 mt-2 pt-2 space-y-2">
              <button
                onClick={() => {
                  navigate('/app/settings/workspaces');
                  setIsOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
              >
                Manage Workspaces
              </button>
              
              {selectedId && selectedId !== currentWorkspace.id && (
                <button
                  onClick={handleSwitchConfirm}
                  className="w-full px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                >
                  Switch Workspace
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}